import React, { useState } from 'react';
import { Button, Dropdown, message, Radio, Table, Tag } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { DeliveryTask, Driver } from '../types/DeliveryTaskType';
import { CHANGE_DELIVERY_TASK_DRIVER } from '../graphql/mutations/changeDeliveryTaskDriver';
import {
    CHANGE_AUTO_ROUTE_DRIVER,
    ChangeAutoRouteDriverResponse,
    ChangeAutoRouteDriverVariables,
} from '../../splitOrder/graphql/template/changeAutoRouteDriver';
import { GET_DELIVERY_TASKS } from '../graphql/queries/getDeliveryTasks';
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import dayjs from 'dayjs';

// 后端返回树状结构，DeliveryTask 已包含 isGroupRow 和 children 字段

interface DeliveryTaskTableProps {
  data: DeliveryTask[];
  loading: boolean;
  currentPage: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  onPageChange: (page: number, size?: number) => void;
  onViewDetail: (taskId: string) => void;
  onAllocateParcels: (taskId: string) => void;
  activeDrivers: Driver[];
  onDriverChange: () => void;
  filters?: any; // 用于 refetchQueries 时传递变量
  isDriverVerificationEnabled?: boolean;
}

const DeliveryTaskTable: React.FC<DeliveryTaskTableProps> = ({
  data,
  loading,
  currentPage,
  pageSize,
  totalElements,
  totalPages,
  onPageChange,
  onViewDetail,
  onAllocateParcels,
  activeDrivers,
  onDriverChange,
  filters,
  isDriverVerificationEnabled
}) => {
  const { t } = useTranslation();
  const headerText = (text: string) => (
    <span
      style={{
        // 头部始终允许换行，避免外语/中文在窄列下被截断
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        lineHeight: 1.2,
        display: 'inline-block'
      }}
    >
      {text}
    </span>
  );
  const [driverChangeVisible, setDriverChangeVisible] = useState<{ [key: string]: boolean }>({});
  const [driverSearchText, setDriverSearchText] = useState<{ [key: string]: string }>({});
  const [changingDriverTaskId, setChangingDriverTaskId] = useState<string | null>(null);
  
  // 跟踪所有父行的 key，用于强制展开
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const [changeDriver] = useMutation(CHANGE_DELIVERY_TASK_DRIVER, {
    awaitRefetchQueries: true
  });

  const [changeAutoRouteDriver] = useMutation<ChangeAutoRouteDriverResponse, ChangeAutoRouteDriverVariables>(CHANGE_AUTO_ROUTE_DRIVER);

  const handleDriverChange = async (taskId: string, newDriverAccountId: string) => {
    // 防止重复点击：在下拉框关闭前就设置状态，避免用户快速连续点击
    if (changingDriverTaskId === taskId) {
      return;
    }
    
    // 验证司机是否已实名
    const selectedDriver = activeDrivers.find(d => d.accountId === Number(newDriverAccountId));
    if (selectedDriver && selectedDriver.verificationStatus !== 2) {
      message.error('未实名司机不可排线');
      return;
    }
    
    // 立即设置状态，防止在下拉框关闭前重复点击
    setChangingDriverTaskId(taskId);
    // 立即关闭选择框
    setDriverChangeVisible(prev => ({ ...prev, [taskId]: false }));
    
    try {
      await changeDriver({
        variables: {
          taskId: Number(taskId),
          newDriverAccountId: Number(newDriverAccountId),
          // 使用前端本地当天日期作为任务日期（YYYY-MM-DD）
          taskDate: dayjs().format('YYYY-MM-DD')
        },
        refetchQueries: [{
          query: GET_DELIVERY_TASKS,
          variables: {
            filter: filters,
            pageRequest: {
              page: currentPage,
              size: pageSize
            }
          }
        }]
      });
      message.success(t('delivery.tasks.message.changeDriverSuccess'));
      // refetchQueries 会自动刷新列表，不需要手动调用 onDriverChange
    } catch (error: any) {
      console.error('Change driver error:', error);
      
      const graphQLError = error?.graphQLErrors?.[0];
      const messageKey = graphQLError?.extensions?.messageKey;
      const details = graphQLError?.extensions?.details || {};
      
      // 如果有 messageKey，使用翻译；翻译失败则用默认错误消息
      let errorMessage = graphQLError?.message || error?.message || t('delivery.tasks.message.changeDriverFailed');
      if (messageKey) {
        const translated = String(t(messageKey, details));
        if (translated !== messageKey) {
          errorMessage = translated;
        }
      }
      message.error(errorMessage);
    } finally {
      setChangingDriverTaskId(null);
    }
  };

  const handleDriverSearch = (taskId: string, value: string) => {
    setDriverSearchText(prev => ({ ...prev, [taskId]: value }));
  };

  const filteredDrivers = (taskId: string) => {
    const searchText = driverSearchText[taskId] || '';
    
    // 递归查找任务记录（包括子行）
    const findTask = (tasks: DeliveryTask[]): DeliveryTask | undefined => {
      for (const task of tasks) {
        if (task.id == taskId) {
          return task;
        }
        if (task.children && task.children.length > 0) {
          const found = findTask(task.children);
          if (found) return found;
        }
      }
      return undefined;
    };
    
    const taskRecord = findTask(data);
    const taskDspId = taskRecord?.dspId;
    
    // 如果任务有dspId，只显示该DSP的司机
    // 使用 == 进行类型转换比较（GraphQL 返回的 dspId 可能是字符串或数字）
    const driversToFilter = taskDspId !== undefined && taskDspId !== null
      ? activeDrivers.filter(driver => driver.dspId != null && driver.dspId == taskDspId)
      : activeDrivers;
    
    return driversToFilter.filter(driver => {
      // 过滤离职司机（isEmployed === false）
      if (driver.isEmployed == false) {
        return false;
      }
      // 搜索过滤
      return driver.name.toLowerCase().includes(searchText.toLowerCase()) ||
        driver.phone.includes(searchText);
    });
  };

  const getDriverName = (driverId: number) => {
    const driver = activeDrivers.find(d => d.accountId === driverId);
    return driver ? `${driver.name} (${driver.phone})` : `Driver ${driverId}`;
  };

  const getDriverDisplay = (driverId: number): { name: string; phone?: string } => {
    const driver = activeDrivers.find(d => d.accountId === driverId);
    if (!driver) return { name: `Driver ${driverId}` };
    return { name: driver.name, phone: driver.phone };
  };

  // 后端已返回树状结构，直接使用（Table 会自动识别 children 字段）
  // 确保子任务不会出现在主列表中（只应该出现在父行的 children 中）
  // ✅ 修复：后端可能返回重复的父行，需要去重
  const tableData = React.useMemo(() => {
    // 收集所有子任务的 ID
    const childTaskIds = new Set<string>();
    const seenParentRowIds = new Set<string>(); // 用于去重父行
    
    data.forEach(task => {
      if (task.isGroupRow && task.children) {
        task.children.forEach(child => {
          childTaskIds.add(child.id);
        });
      }
    });
    
    // 过滤：只保留父行和单条任务（排除所有子任务），并对父行去重
    return data.filter(task => {
      if (task.isGroupRow) {
        // ✅ 父行去重：如果已经见过这个父行ID，跳过
        if (seenParentRowIds.has(task.id)) {
          return false;
        }
        seenParentRowIds.add(task.id);
        return true;
      }
      // 如果不是父行，且不是子任务，则保留（单条任务）
      return !childTaskIds.has(task.id);
    });
  }, [data]);

  // 构建子行到父行的映射，避免每次渲染都遍历
  const childToParentMap = React.useMemo(() => {
    const map = new Map<string, string>();
    data.forEach(row => {
      if (row.isGroupRow && row.children) {
        row.children.forEach(child => {
          map.set(child.id, row.id);
        });
      }
    });
    return map;
  }, [data]);

  // 当数据变化时，更新展开的行（所有父行）
  React.useEffect(() => {
    const parentRowKeys = tableData
      .filter(row => row.isGroupRow === true)
      .map(row => row.id);
    setExpandedRowKeys(parentRowKeys);
  }, [tableData]);

  // 根据展开状态和行类型动态设置样式
  const getRowClassName = (record: DeliveryTask): string => {
    if (record.isGroupRow === true) {
      // 父行：展开时深色，收缩时浅灰色
      const isExpanded = expandedRowKeys.includes(record.id);
      return isExpanded ? 'bg-gray-100' : 'bg-gray-50';
    }
    // 检查是否是子行（使用缓存的映射）
    const parentRowId = childToParentMap.get(record.id);
    if (parentRowId && expandedRowKeys.includes(parentRowId)) {
      // 子行：父行展开时浅灰色
      return 'bg-gray-50';
    }
    // 单条任务（不是子行）：默认背景
    return '';
  };

  const columns = [
    {
      title: t('delivery.tasks.label.taskId'),
      dataIndex: 'id',
      key: 'id',
      align: 'left' as const,
      render: (id: string, record: DeliveryTask) => {
        if (record.isGroupRow) {
          return null; // 父行不显示
        }
        return (
          <Button
            type="link"
            onClick={() => onViewDetail(id)}
            style={{ paddingLeft: 0, whiteSpace: 'nowrap' }}
          >
            {id}
          </Button>
        );
      }
    },
    {
      title: t('delivery.tasks.label.route'),
      dataIndex: 'routeName',
      key: 'routeName',
      align: 'left' as const,
      width: 70,
      ellipsis: true,
      render: (routeName: string, record: DeliveryTask) => {
        if (record.isGroupRow) {
          return <span className="font-semibold">{routeName}</span>; // 父行显示区域名
        }
        return <span className="truncate block">{routeName}</span>;
      }
    },
    {
      title: headerText(t('delivery.tasks.label.workIndex')),
      dataIndex: 'workIndex',
      key: 'workIndex',
      align: 'left' as const,
      width: 100,
      render: (workIndex: string, record: DeliveryTask) => {
        if (record.isGroupRow) return null;
        return workIndex || '-';
      }
    },
    {
      title: t('delivery.tasks.label.driver'),
      dataIndex: 'driverAccountId',
      key: 'driver',
      align: 'left' as const,
      width: 200,
      render: (driverId: number, record: DeliveryTask) => {
        const isNegativeDriver = driverId != null && driverId < 0;

        // 父行只显示司机名称
        if (record.isGroupRow) {
          if (isNegativeDriver) {
            return (
              <div className="min-w-0">
                <div className="font-semibold truncate text-red-500">{t('delivery.tasks.label.unassignedDriver')}</div>
              </div>
            );
          }
          const driver = getDriverDisplay(driverId);
          return (
            <div className="min-w-0">
              <div className="font-semibold truncate">{driver.name}</div>
              {driver.phone ? (
                <div className="text-xs text-gray-500 truncate">{driver.phone}</div>
              ) : null}
            </div>
          );
        }

        // 子行显示完整司机信息（包含更换司机按钮）
        const taskRecord = record;
        const isCompleted = taskRecord.status === 'COMPLETED';
        const hasDriverTask = taskRecord.driverTaskId != null && taskRecord.driverTaskId > 0;

        const buildDriverMenu = (onSelect: (accountId: string, driverName: string) => void) => (
          <div className="w-100 bg-white border border-gray-200 rounded-lg shadow-lg">
            <div className="p-2 border-b">
              <input
                type="text"
                placeholder={t('delivery.tasks.label.searchDriverPlaceholder') as string}
                value={driverSearchText[taskRecord.id] || ''}
                onChange={(e) => handleDriverSearch(taskRecord.id, e.target.value)}
                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="p-3">
              <Radio.Group
                value={isNegativeDriver ? undefined : String(taskRecord.driverAccountId)}
                onChange={(e) => onSelect(e.target.value, filteredDrivers(taskRecord.id).find(d => String(d.accountId) === e.target.value)?.name || '')}
                className="w-full"
                disabled={changingDriverTaskId === taskRecord.id}
              >
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {filteredDrivers(taskRecord.id).length > 0 ? (
                    filteredDrivers(taskRecord.id).map(driver => {
                      const isVerified = driver.verificationStatus == 2;
                      const isBlacklisted = !!driver.isBlacklist;
                      return (
                        <div key={driver.id} className="flex items-center">
                          <Radio
                            value={String(driver.accountId)}
                            className="mr-2"
                            disabled={isBlacklisted || changingDriverTaskId === taskRecord.id || (isDriverVerificationEnabled && !isVerified)}
                          >
                            <span className="font-medium">{driver.name}</span>
                            {isBlacklisted ? (
                              <span style={{ fontSize: '12px', color: '#faad14', marginLeft: '8px' }}>
                                {t('delivery.routes.label.driverBlacklistedTag')}
                              </span>
                            ) : isDriverVerificationEnabled && isVerified ? (
                              <span style={{ fontSize: '12px', color: '#52c41a', marginLeft: '8px' }}>
                                {t('delivery.routes.label.driverVerified')}
                              </span>
                            ) : isDriverVerificationEnabled && !isVerified ? (
                              <span style={{ fontSize: '12px', color: '#faad14', marginLeft: '8px' }}>
                                {t('delivery.routes.label.driverUnverifiedShort')}
                              </span>
                            ) : null}
                          </Radio>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-gray-400 text-center py-2">{t('delivery.tasks.label.noMatchingDrivers')}</div>
                  )}
                </div>
              </Radio.Group>
            </div>
          </div>
        );

        if (isNegativeDriver) {
          const driverMenu = buildDriverMenu(async (accountId, selectedDriverName) => {
            if (changingDriverTaskId === taskRecord.id) return;
            const selectedDriver = activeDrivers.find(d => d.accountId === Number(accountId));
            if (selectedDriver && selectedDriver.verificationStatus !== 2) {
              message.error('未实名司机不可排线');
              return;
            }
            setChangingDriverTaskId(taskRecord.id);
            setDriverChangeVisible(prev => ({ ...prev, [taskRecord.id]: false }));
            try {
              await changeAutoRouteDriver({
                variables: {
                  driverTaskId: taskRecord.driverTaskId!,
                  driverAccountId: Number(accountId),
                  driverName: selectedDriverName,
                },
              });
              message.success(t('delivery.tasks.message.changeDriverSuccess'));
              onDriverChange();
            } catch (error: any) {
              const graphQLError = error?.graphQLErrors?.[0];
              const messageKey = graphQLError?.extensions?.messageKey;
              const details = graphQLError?.extensions?.details || {};
              let errorMessage = graphQLError?.message || error?.message || t('delivery.tasks.message.changeDriverFailed');
              if (messageKey) {
                const translated = String(t(messageKey, details));
                if (translated !== messageKey) {
                  errorMessage = translated;
                }
              }
              message.error(errorMessage);
            } finally {
              setChangingDriverTaskId(null);
            }
          });

          return (
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="truncate text-red-500">{t('delivery.tasks.label.unassignedDriver')}</div>
              </div>
              {!isCompleted && hasDriverTask && (
                <Dropdown
                  overlay={driverMenu}
                  trigger={['click']}
                  visible={driverChangeVisible[taskRecord.id]}
                  onVisibleChange={(visible) => {
                    if (!visible || changingDriverTaskId !== taskRecord.id) {
                      setDriverChangeVisible(prev => ({ ...prev, [taskRecord.id]: visible }));
                    }
                  }}
                  placement="bottomRight"
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<SwapOutlined />}
                    loading={changingDriverTaskId === taskRecord.id}
                    disabled={changingDriverTaskId === taskRecord.id}
                    className="text-blue-500 hover:text-blue-600 hover:bg-blue-50 p-1 h-6 w-6 flex items-center justify-center"
                  />
                </Dropdown>
              )}
            </div>
          );
        }

        const driver = getDriverDisplay(driverId);
        const driverMenu = buildDriverMenu((accountId) => handleDriverChange(taskRecord.id, accountId));

        return (
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="truncate">{driver.name}</div>
              {driver.phone ? (
                <div className="text-xs text-gray-500 truncate">{driver.phone}</div>
              ) : null}
            </div>
            {!isCompleted && (
              <Dropdown
                overlay={driverMenu}
                trigger={['click']}
                visible={driverChangeVisible[taskRecord.id]}
                onVisibleChange={(visible) => {
                  if (!visible || changingDriverTaskId !== taskRecord.id) {
                    setDriverChangeVisible(prev => ({ ...prev, [taskRecord.id]: visible }));
                  }
                }}
                placement="bottomRight"
              >
                <Button
                  type="text"
                  size="small"
                  icon={<SwapOutlined />}
                  loading={changingDriverTaskId === taskRecord.id}
                  disabled={changingDriverTaskId === taskRecord.id}
                  className="text-blue-500 hover:text-blue-600 hover:bg-blue-50 p-1 h-6 w-6 flex items-center justify-center"
                />
              </Dropdown>
            )}
            {isCompleted && (
              <SwapOutlined className="text-gray-300" />
            )}
          </div>
        );
      }
    },
    {
      title: headerText(String(t('delivery.tasks.label.taskDate'))),
      dataIndex: 'taskDate',
      key: 'taskDate',
      align: 'left' as const,
      width: 120,
      render: (taskDate: string, record: DeliveryTask) => {
        if (record.isGroupRow) {
          return <span className="font-semibold" style={{ whiteSpace: 'nowrap' }}>{taskDate}</span>; // 父行显示任务日期
        }
        return <span style={{ whiteSpace: 'nowrap' }}>{taskDate}</span>;
      }
    },
    {
      title: headerText(String(t('delivery.tasks.label.actualPickTransfer'))),
      key: 'pickTransfer',
      align: 'left' as const,
      width: 110,
      render: (_: any, record: DeliveryTask) => {
        if (record.isGroupRow) {
          return null; // 父行不显示
        }
        const taskRecord = record;
        // 显示实际需要派送的包裹数量（排除转出的包裹）
        return <div className="text-left">{taskRecord.actualTotalParcels ?? 0}</div>;
      }
    },
    {
      title: headerText(String(t('delivery.tasks.label.actualPickup'))),
      key: 'actualPickup',
      dataIndex: 'actualPickedUpParcels',
      align: 'left' as const,
      width: 80,
      render: (value: number | undefined, record: DeliveryTask) => {
        if (record.isGroupRow) {
          return null; // 父行不显示
        }
        return value ?? 0;
      }
    },
    {
      title: headerText(String(t('delivery.tasks.label.assignedParcels'))),
      dataIndex: 'assignedParcels',
      key: 'assignedParcels',
      align: 'left' as const,
      width: 80,
      render: (value: number | undefined, record: DeliveryTask) => {
        if (record.isGroupRow) {
          return null; // 父行不显示
        }
        return value;
      }
    },
    {
      title: headerText(String(t('delivery.tasks.label.estimatedDistanceM'))),
      dataIndex: 'estimatedDistanceKm',
      key: 'estimatedDistanceKm',
      align: 'left' as const,
      width: 80,
      render: (value: number | undefined, record: DeliveryTask) => {
        if (record.isGroupRow) {
          return null; // 父行不显示
        }
        if (value == null || value === 0) return '0';
        // 后端存储的是km，转换为英里显示（与路由页面保持一致）
        const miles = value * 0.621371;
        return `${miles.toFixed(1)}`;
      }
    },
    {
      title: headerText(String(t('delivery.tasks.label.estimatedDurationH'))),
      dataIndex: 'estimatedDurationMinutes',
      key: 'estimatedDurationMinutes',
      align: 'left' as const,
      width: 80,
      render: (value: number | undefined, record: DeliveryTask) => {
        if (record.isGroupRow) {
          return null; // 父行不显示
        }
        if (value == null || value === 0) return '0h 0m';
        const hours = Math.floor(value / 60);
        const minutes = value % 60;
        return `${hours}h ${minutes}m`;
      }
    },
    {
      title: t('delivery.tasks.label.status'),
      dataIndex: 'status',
      key: 'status',
      align: 'left' as const,
      width: 120,
      render: (status: string | undefined, record: DeliveryTask) => {
        if (record.isGroupRow) {
          return null; // 父行不显示
        }
        if (!status) return null;
        // NONE 状态仅用于展示，显示为 "-"
        if (status === 'NONE') {
          return '-';
        }
        const colorMap: { [key: string]: string } = {
          'CREATED': 'blue',
          'DRIVER_PICKINGUP': 'orange',
          'DELIVERING': 'green',
          'COMPLETED': 'default'
        };
        return <Tag color={colorMap[status] || 'default'}>{t(`delivery.tasks.status.${status}`)}</Tag>;
      }
    },
    {
      title: t('delivery.tasks.label.createBy'),
      dataIndex: 'createdByName',
      key: 'createdByName',
      align: 'left' as const,
      width: 140,
      ellipsis: true,
      render: (value: string | undefined, record: DeliveryTask) => {
        if (record.isGroupRow) {
          return null; // 父行不显示
        }
        return <span className="truncate block">{value || '-'}</span>;
      }
    },
    {
      title: headerText(String(t('delivery.tasks.label.createTime'))),
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'left' as const,
      width: 170,
      render: (time: string | undefined, record: DeliveryTask) => {
        if (record.isGroupRow) {
          return null; // 父行不显示
        }
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {time ? new Date(time).toLocaleString() : '-'}
          </span>
        );
      }
    },
    {
      title: t('delivery.tasks.label.action'),
      key: 'action',
      align: 'left' as const,
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: DeliveryTask) => {
        if (record.isGroupRow) {
          return null; // 父行不显示
        }
        const taskRecord = record;
        return (
          <div className="flex gap-2 justify-start whitespace-nowrap">
            <Button
              type="link"
              onClick={() => onViewDetail(taskRecord.id)}
            >
              {t('delivery.tasks.label.viewDetail')}
            </Button>
            <Button
              type="link"
              onClick={() => onAllocateParcels(taskRecord.id)}
              disabled={taskRecord.status === 'COMPLETED'}
              title={taskRecord.status === 'COMPLETED'
                ? 'Completed tasks cannot be transferred'
                : ''}
            >
              {t('delivery.tasks.label.allocateParcels')}
            </Button>
          </div>
        );
      }
    }
  ];

  return (
    <div>
      <Table
        columns={columns}
        dataSource={tableData}
        loading={loading}
        rowKey="id"
        pagination={false}
        tableLayout="fixed"
        // 避免列总宽度小于固定 scroll.x 时，横向滚动到最右侧出现空白区域
        scroll={{ x: 'max-content' }}
        className="mb-4"
        rowClassName={getRowClassName}
        expandable={{
          // Table 会自动识别 children 字段并渲染树形结构
          defaultExpandAllRows: true, // 默认展开所有行
          expandedRowKeys: expandedRowKeys, // 受控模式：使用状态控制展开的行
          onExpandedRowsChange: (keys) => {
            // 允许用户手动展开/收缩，更新状态
            setExpandedRowKeys(keys as string[]);
          },
          expandRowByClick: false, // 不允许通过点击行本身来展开/收缩（只能点击图标）
          // 不需要 expandedRowRender，Table 会自动渲染 children
          rowExpandable: (record: DeliveryTask) => {
            // 只有父行（有 children）可以展开
            return !!(record.isGroupRow === true && record.children && record.children.length > 0);
          },
          indentSize: 20 // 子行缩进，方便区分层级
        }}
      />

      {/* 分页 */}
      <TablePaginationBar
        total={totalElements}
        current={currentPage}
        pageSize={pageSize}
        onChange={(page, size) => onPageChange(page, size)}
        onShowSizeChange={(page, size) => onPageChange(page, size)}
      />
    </div>
  );
};

export default DeliveryTaskTable;